import { apiFetch } from "@core/http";
import type { components } from "@core/types/openapi";

// PLAN-007 §7 (fase 3): reemplaza el `<input placeholder="https://…">` por el
// selector de archivo que ya describía `context/prototype/source/admin.jsx`.
// Flujo de dos fases (ADR-034 §3, `api/contracts/parts/70-files.yaml`):
//
//   1. POST /v1/portal/files/upload-url  → { fileId, uploadUrl, expiresIn }
//   2. PUT directo a S3 con `uploadUrl`
//   3. POST /v1/portal/files/{id}/complete → { fileId, imageUrl }
//
// `entityType` es el conjunto cerrado del contrato (`FileEntityType`); no
// lleva `entityId` porque la entidad todavía no existe al pedir la URL.
export type FileEntityType = components["schemas"]["FileEntityType"];

export interface UploadedImage {
  readonly fileId: string;
  readonly imageUrl: string;
}

// ADR-034 §7 / api/contracts/parts/70-files.yaml: tope de 5 MB impuesto en
// la firma del PUT. Chequear acá antes de pedir la URL evita una ida y
// vuelta al servidor por nada (brief §4).
export const MAX_UPLOAD_SIZE_BYTES = 5 * 1024 * 1024;

// Mismos tres códigos que el backend puede responder en el flujo de subida
// (contrato `70-files.yaml`), traducidos a un mensaje que el operador
// entiende. Cada pantalla los combina con su propio mapa de
// `SERVER_ERROR_MESSAGES` (mismo patrón que `errorMessage.ts`). Constantes
// propias (no solo entradas del mapa) porque `uploadImage` necesita el texto
// de `FILE_TOO_LARGE` sin pasar por un acceso indexado, que
// `noUncheckedIndexedAccess` tipa como `string | undefined`.
const FILE_TOO_LARGE_MESSAGE = "El archivo supera el límite de 5 MB";
const UNSUPPORTED_FILE_TYPE_MESSAGE = "Ese tipo de archivo no está admitido";
const UPLOAD_NOT_COMPLETED_MESSAGE =
  "La subida no llegó a completarse; intenta de nuevo";

export const UPLOAD_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  FILE_TOO_LARGE: FILE_TOO_LARGE_MESSAGE,
  UNSUPPORTED_FILE_TYPE: UNSUPPORTED_FILE_TYPE_MESSAGE,
  UPLOAD_NOT_COMPLETED: UPLOAD_NOT_COMPLETED_MESSAGE,
};

// Error sintético para el chequeo de tamaño en cliente: mismo `code` que
// usaría el rechazo del servidor (`FILE_TOO_LARGE`), para que cualquier
// resolutor de errores que ya mapea `UPLOAD_ERROR_MESSAGES` por `code` lo
// traduzca igual sin importar si el rechazo vino del cliente o del servidor.
export class ClientFileValidationError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = "ClientFileValidationError";
    this.code = code;
  }
}

function requestUploadUrl(file: File, entityType: FileEntityType) {
  return apiFetch<components["schemas"]["FileUploadUrl"]>(
    "/v1/portal/files/upload-url",
    {
      method: "POST",
      body: {
        originalFilename: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        entityType,
      },
    },
  );
}

// PLAN-007 §7.3.1: el PUT a S3 es cross-origin, con cuerpo binario y **sin
// credenciales** — mandarle la cookie de sesión del portal a Amazon sería un
// error de seguridad. Por eso es una función aparte que solo hace `fetch`
// directo contra `uploadUrl`, nunca por `@core/http/client` (que siempre
// agrega cookie + CSRF). El `Content-Type` tiene que ser exactamente el que
// se firmó, y el cuerpo el `file` elegido sin transformar: S3 firma también
// el `Content-Length` exacto y rechaza con 403 cualquier otra cosa (ADR-034
// §7).
export async function putFileToStorage(
  uploadUrl: string,
  file: File,
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!response.ok) {
    throw new Error(
      `No se pudo subir el archivo a almacenamiento (status ${String(response.status)})`,
    );
  }
}

function completeFileUpload(fileId: string) {
  return apiFetch<components["schemas"]["FileCompleted"]>(
    `/v1/portal/files/${fileId}/complete`,
    { method: "POST" },
  );
}

/**
 * Orquesta el flujo completo de subida (brief §3): pide la URL firmada, sube
 * el archivo directo a S3 y confirma. Devuelve el `fileId` y la `imageUrl`
 * ya firmada que el formulario debe mostrar.
 *
 * Lanza `ClientFileValidationError` si el archivo supera el tope antes de
 * gastar una ida y vuelta al servidor, o `ApiError` si el servidor rechaza
 * cualquiera de los tres pasos (`UPLOAD_ERROR_MESSAGES` trae los mensajes
 * para los códigos que puede devolver).
 */
export async function uploadImage(
  file: File,
  entityType: FileEntityType,
): Promise<UploadedImage> {
  if (file.size > MAX_UPLOAD_SIZE_BYTES) {
    throw new ClientFileValidationError(
      "FILE_TOO_LARGE",
      FILE_TOO_LARGE_MESSAGE,
    );
  }
  const { fileId, uploadUrl } = await requestUploadUrl(file, entityType);
  await putFileToStorage(uploadUrl, file);
  const completed = await completeFileUpload(fileId);
  return { fileId: completed.fileId, imageUrl: completed.imageUrl };
}
